import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiCheckCircle, HiOutlineChatBubbleLeftRight, HiArrowUpRight } from 'react-icons/hi2';
import Swal from 'sweetalert2';
import { saveEnquiry } from '../data/enquiriesData';
import {
  getDivisionCategories,
  getDivisionProducts
} from '../data/divisionProducts';

const applications = [
  'Metal Fabrication', 'CNC Machining', 'Sheet Metal Works', 'Structural Steel Cutting',
  'Automotive Component Manufacturing', 'Precision Engineering', 'Pipeline Works', 'Tool Room Operations',
];

const staticProductRange = [
  { name: 'Annular Cutters', desc: 'HSS and TCT annular cutters for magnetic drill and milling machines.' },
  { name: 'End Mills & Slot Drills', desc: 'Solid carbide and HSS end mills for CNC milling centres.' },
  { name: 'Hole Saws', desc: 'Bi-metal and carbide-tipped hole saws for a range of materials.' },
  { name: 'Taps & Dies', desc: 'Hand and machine taps, dies, and threading accessories.' },
  { name: 'Insert Tooling', desc: 'Indexable carbide inserts for turning, milling, and grooving.' },
  { name: 'Boring Tools', desc: 'Adjustable boring heads and boring bars for precision bore work.' },
];

const benefits = [
  { title: 'Premium Grade Materials', desc: 'All tools manufactured from HSS, solid carbide, or tungsten carbide for extended tool life.' },
  { title: 'Wide Application Range', desc: 'Suitable for cutting mild steel, stainless steel, aluminium, cast iron, and exotic alloys.' },
  { title: 'Competitive Pricing', desc: 'Direct supply from trusted manufacturers with volume discount structures.' },
  { title: 'Technical Consultation', desc: 'Our team advises on correct tool selection, feeds, and speeds for your application.' },
  { title: 'Quick Availability', desc: 'Most standard sizes held in stock for immediate dispatch.' },
  { title: 'After-Sales Support', desc: 'Regrinding and reconditioning services available for select tool types.' },
];

export default function CuttingTools() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', city: '', message: '' });

  const loadData = async () => {
    const cats = await getDivisionCategories('cutting');
    const prods = await getDivisionProducts('cutting');
    setCategories(cats);
    setProducts(prods);
    if (cats.length > 0) {
      setActiveTab(prev => {
        if (prev && cats.some(c => c.name === prev)) return prev;
        return cats[0].name;
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, []);

  const handleChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneDigits = formData.phone.trim();
    if (!/^\d{10}$/.test(phoneDigits)) {
      Swal.fire({
        title: 'Invalid Phone Number',
        text: 'Please enter a valid 10-digit number.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      Swal.fire({
        title: 'Invalid Email Address',
        text: 'Please enter a valid email address.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    const newEnquiry = {
      id: `enq-${Date.now()}`,
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      city: formData.city,
      message: formData.message || 'Cutting Tools enquiry request.',
      type: 'cutting-tools',
      status: 'pending',
      date: new Date().toISOString().replace('T', ' ').slice(0, 16)
    };

    try {
      await saveEnquiry(newEnquiry, true);
      Swal.fire({ title: 'Enquiry Received!', text: `Thank you, ${formData.name}. Our tooling specialist will call you within 24 hours.`, icon: 'success', confirmButtonColor: '#0F5A3A' });
      setFormData({ name: '', phone: '', email: '', city: '', message: '' });
      e.target.reset();
    } catch (err) {
      Swal.fire('Error', 'Failed to submit enquiry. Please try again.', 'error');
    }
  };

  const filtered = products.filter(p => p.category === activeTab);

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* Hero */}
      <section
        className="relative text-white py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/Cutting Tools.png')` }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">Tooling Division</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Cutting <span className="text-gradient-gold">Tools</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Premium industrial cutting tools, precision machining tooling, and customised tooling solutions for metal fabrication and CNC operations.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-3.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center gap-2 text-xs text-body-muted">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span className="text-border-dark">/</span>
          <span>Divisions</span>
          <span className="text-border-dark">/</span>
          <span className="font-semibold text-primary">Cutting Tools</span>
        </div>
      </div>

      {/* Product Range Overview */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <span className="eyebrow">Overview</span>
            <h2 className="text-3xl md:text-4xl font-display font-extrabold text-heading mb-6">Industrial Precision Tooling Solutions</h2>
            <div className="space-y-3 text-sm text-body leading-relaxed">
              <p>Green Gusto is an authorized distributor for premium cutting tool brands, supplying HSS, carbide, and tungsten-tipped tooling solutions to manufacturing plants, tool rooms, and fabrication workshops across Maharashtra.</p>
              <p>From annular cutters and end mills to indexable insert tooling and custom holder assemblies, we stock a comprehensive range for every metal cutting application.</p>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {[{ v: '500+', l: 'SKUs Available' }, { v: 'HSS/Carbide + PCD', l: 'Material Grades' }, { v: 'Same-Day', l: 'Stock Dispatch' }, { v: 'Custom', l: 'Tool Holders' }].map((s, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-5 text-center shadow-card hover:shadow-card-hover hover:-translate-y-1 hover:border-accent/20 transition-all duration-300">
                <span className="text-lg font-display font-extrabold text-primary block mb-1">{s.v}</span>
                <span className="text-[10px] text-body-muted font-bold uppercase tracking-wider">{s.l}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Brands Section */}
        <div className="mt-16 pt-12 border-t border-border/80">
          <div className="text-center mb-10">
            <span className="eyebrow">Our Partners</span>
            <h3 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Authorized Brands We Represent</h3>
          </div>
          <div className="grid grid-cols-4 items-center justify-items-center gap-3 sm:gap-6 md:gap-8 lg:gap-10 max-w-6xl mx-auto px-2">
            <a
              href="https://guhring.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center p-2 hover:scale-105 transition-all duration-300 group w-full"
            >
              <img
                src="/Guhring.png"
                alt="Guhring Logo"
                className="h-12 sm:h-16 md:h-20 lg:h-24 w-auto max-w-full object-contain transition-all duration-300"
              />
            </a>
            <a
              href="https://www.bechemindia.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center p-2 hover:scale-105 transition-all duration-300 group w-full"
            >
              <img
                src="/Bechem.png"
                alt="Bechem Logo"
                className="h-12 sm:h-16 md:h-20 lg:h-24 w-auto max-w-full object-contain transition-all duration-300"
              />
            </a>
            <a
              href="https://www.ajgroupco.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center p-2 hover:scale-105 transition-all duration-300 group w-full"
            >
              <img
                src="/AJ Sales.png"
                alt="AJ Sales Logo"
                className="h-12 sm:h-16 md:h-20 lg:h-24 w-auto max-w-full object-contain transition-all duration-300"
              />
            </a>
            <div className="flex items-center justify-center p-2 hover:scale-105 transition-all duration-300 group w-full">
              <img
                src="/Regal Technology.png"
                alt="Regal Technology Logo"
                className="h-12 sm:h-16 md:h-20 lg:h-24 w-auto max-w-full object-contain transition-all duration-300"
              />
            </div>
          </div>
        </div>

        {/* Product Range Grid */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <span className="eyebrow">Our Range</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Tool Categories</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {staticProductRange.map((pr, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-6 shadow-card hover:shadow-card-hover hover:-translate-y-1 hover:border-primary/20 transition-all duration-300 group">
                <div className="w-8 h-0.5 bg-accent rounded-full mb-4" />
                <h4 className="text-xs font-bold text-heading uppercase tracking-wide mb-2 group-hover:text-primary transition-colors">{pr.name}</h4>
                <p className="text-xs text-body leading-relaxed">{pr.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Catalog */}
      <section className="bg-light-section border-y border-border py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <span className="eyebrow">Browse Products</span>
            <h2 className="text-2xl font-display font-extrabold text-heading">Cutting Tool Products</h2>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-3 mb-10">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.name)}
                className={`px-6 py-3 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === cat.name
                    ? 'bg-primary text-white shadow-premium'
                    : 'bg-white border border-border text-body hover:border-primary/30 hover:text-primary shadow-card'
                  }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filtered.map(prod => (
              <div key={prod.id} className="bg-white border border-border rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover hover:-translate-y-1.5 transition-all duration-300 flex flex-col sm:flex-row group min-h-[250px]">
                <div className="w-full sm:w-48 h-48 sm:h-auto flex-shrink-0 bg-light-section border-b sm:border-b-0 sm:border-r border-border overflow-hidden flex items-center justify-center">
                  <img src={prod.image || '/Divisions/cuting tool.jpg'} alt={prod.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-grow flex flex-col justify-between">
                  <div>
                    <span className="badge-gold text-xs mb-2.5 block w-fit">{prod.category}</span>
                    <h3 className="text-base font-bold text-slate-900 mb-1.5 group-hover:text-primary transition-colors">{prod.name}</h3>
                    <p className="text-xs text-body leading-relaxed mb-3">{prod.desc}</p>
                    {prod.specs && Object.keys(prod.specs).length > 0 && (
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {Object.entries(prod.specs).map(([k, v]) => (
                          <div key={k} className="bg-light-section rounded-lg px-2.5 py-1.5">
                            <span className="text-[9px] text-body-muted block uppercase tracking-wide truncate">{k}</span>
                            <span className="text-xs font-bold text-primary block truncate mt-0.5">{v}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 mt-4 pt-2">
                    <button
                      onClick={() => document.getElementById('tools-form')?.scrollIntoView({ behavior: 'smooth' })}
                      className="btn-primary w-full justify-center py-2.5 rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all"
                    >
                      Enquire Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits + Applications + Form Grid */}
      <section id="tools-form" className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

          {/* Left Column: Benefits (top) & Applications (bottom) */}
          <div className="lg:col-span-6 space-y-12">

            {/* Benefits */}
            <div>
              <span className="eyebrow">Benefits</span>
              <h2 className="text-2xl font-display font-extrabold text-heading mb-6">Why Our Tooling?</h2>
              <div className="space-y-4">
                {benefits.slice(0, 4).map((b, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <HiCheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-bold text-heading mb-1">{b.title}</h4>
                      <p className="text-xs text-body leading-relaxed">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Applications */}
            <div>
              <span className="eyebrow">Applications</span>
              <h2 className="text-2xl font-display font-extrabold text-heading mb-6">Industries We Supply</h2>
              <div className="flex flex-wrap gap-2.5">
                {applications.map((a, i) => (
                  <span key={i} className="px-3.5 py-2 bg-white border border-border rounded-full text-xs font-semibold text-body hover:border-primary/30 hover:text-primary hover:shadow-card transition-all duration-200 cursor-default">
                    {a}
                  </span>
                ))}
              </div>
            </div>

          </div>

          {/* Right Column: Enquiry Form */}
          <div className="lg:col-span-6 w-full">
            <div className="bg-charcoal text-white border border-white/5 rounded-3xl p-8 md:p-10 shadow-premium relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-40 h-40 bg-accent/8 rounded-full blur-2xl" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-2">
                  <HiOutlineChatBubbleLeftRight className="w-5 h-5 text-accent" />
                  <span className="text-xs font-bold text-accent uppercase tracking-widest">Tooling Enquiry Form</span>
                </div>
                <h3 className="text-xl font-display font-extrabold text-white mb-2">Request Cutting Tool Specifications</h3>
                <p className="text-xs text-white/60 mb-8">Our tooling specialist will respond within 24 hours.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[['name', 'Full Name', 'text'], ['phone', 'Phone', 'tel'], ['email', 'Email', 'email'], ['city', 'City', 'text']].map(([n, p, t]) => (
                      <div key={n}>
                        <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">{p}</label>
                        <input type={t} name={n} value={formData[n]} onChange={handleChange} required placeholder={p} className="input-dark" />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Message / Requirement</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={3} required placeholder="Describe your tooling requirement..." className="input-dark resize-none" />
                  </div>
                  <button type="submit" className="btn-green rounded-xl w-full justify-center py-3.5 text-sm">
                    Submit Tooling Enquiry
                    <HiArrowUpRight className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
